import React from "react";
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Package, Tags, ShoppingBag, Users, FileText,
  Search as SeoIcon, Palette, Ticket, Star, LogOut, CircleDashed, Gift,
  BarChart3, AlertTriangle, Upload, Download, Rss, ScrollText, BookOpen,
  Coins, Shield, Megaphone, Code2, Truck, Mail, CreditCard,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBranding } from "@/context/BrandingContext";

const menu = [
  { section: "Overview", items: [
    { to: "/admin", icon: LayoutDashboard, label: "Dashboard", end: true },
    { to: "/admin/analytics", icon: BarChart3, label: "Sales analytics" },
  ]},
  { section: "Catalog", items: [
    { to: "/admin/products", icon: Package, label: "Products" },
    { to: "/admin/categories", icon: Tags, label: "Categories" },
    { to: "/admin/stories", icon: CircleDashed, label: "Stories" },
    { to: "/admin/low-stock", icon: AlertTriangle, label: "Low stock" },
    { to: "/admin/bulk-upload", icon: Upload, label: "Bulk upload" },
  ]},
  { section: "Commerce", items: [
    { to: "/admin/orders", icon: ShoppingBag, label: "Orders" },
    { to: "/admin/customers", icon: Users, label: "Customers" },
    { to: "/admin/coupons", icon: Ticket, label: "Coupons" },
    { to: "/admin/payments", icon: CreditCard, label: "Payments" },
    { to: "/admin/rewards", icon: Gift, label: "Reward tiers" },
    { to: "/admin/coins", icon: Coins, label: "Coin wallet" },
  ]},
  { section: "Content", items: [
    { to: "/admin/blogs", icon: FileText, label: "Journal" },
    { to: "/admin/pages", icon: BookOpen, label: "Pages" },
    { to: "/admin/marketing", icon: Megaphone, label: "Marketing" },
    { to: "/admin/seo", icon: SeoIcon, label: "SEO" },
    { to: "/admin/branding", icon: Palette, label: "Branding" },
    { to: "/admin/reviews", icon: Star, label: "Reviews" },
  ]},
  { section: "Operations", items: [
    { to: "/admin/reports", icon: Download, label: "Reports & export" },
    { to: "/admin/feeds", icon: Rss, label: "Feed manager" },
    { to: "/admin/audit", icon: ScrollText, label: "Audit log" },
    { to: "/admin/shipping", icon: Truck, label: "Shipping" },
    { to: "/admin/email", icon: Mail, label: "Email setup" },
    { to: "/admin/tracking", icon: Code2, label: "Code injection" },
    { to: "/admin/security", icon: Shield, label: "Security" },
  ]},
];

const AdminLayout = () => {
  const { adminUser, adminLogout, loading } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();

  if (loading) return <div className="p-10 text-cream/60 bg-[#0F0812] min-h-screen">Loading…</div>;
  if (!adminUser) { navigate("/admin/login"); return null; }

  return (
    <div className="min-h-screen flex" style={{ background: "#0B0610" }} data-testid="admin-layout">
      {/* SIDEBAR */}
      <aside
        className="w-64 flex-shrink-0 hidden md:flex flex-col sticky top-0 h-screen border-r border-white/10 text-cream"
        style={{ background: "linear-gradient(180deg, #140917 0%, #0B0610 100%)" }}
      >
        <div className="p-5 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3" data-testid="admin-logo-link">
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.brand_name || "rieave"}
                className="h-9 w-auto object-contain"
                style={{ filter: "invert(1) brightness(1.1)" }}
              />
            ) : (
              <span
                className="font-hero text-2xl"
                style={{ backgroundImage: "linear-gradient(135deg, #E8B8CE, #C9789A, #D4B896)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}
              >
                {branding.brand_name || "rieave"}
              </span>
            )}
            <span className="font-label text-[10px] uppercase tracking-[0.22em] text-cream/70">admin</span>
          </Link>
        </div>
        <nav className="flex-1 p-3 space-y-5 overflow-y-auto">
          {menu.map((sec) => (
            <div key={sec.section}>
              <p className="px-3 mb-1 font-label text-[10px] uppercase tracking-[0.24em] text-cream/80">{sec.section}</p>
              <div className="space-y-0.5">
                {sec.items.map((m) => (
                  <NavLink
                    key={m.to} to={m.to} end={m.end}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-label tracking-wide transition ${
                        isActive ? "bg-white/10 text-cream shadow-[inset_2px_0_0_0_#C9789A]" : "text-cream/80 hover:bg-white/8 hover:text-cream"
                      }`
                    }
                    data-testid={`admin-nav-${m.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    <m.icon size={15} /> {m.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="text-[10px] uppercase tracking-[0.22em] text-cream/65 mb-1">Signed in as</div>
          <div className="font-cormorant text-sm text-cream truncate mb-3">{adminUser.email}</div>
          <button
            className="flex items-center gap-2 text-sm text-cream/80 hover:text-rose font-label uppercase tracking-[0.2em] text-[11px]"
            onClick={() => { adminLogout(); localStorage.removeItem("rieave.gate.token"); navigate("/admin/login"); }}
            data-testid="admin-logout"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* MAIN */}
      <main className="flex-1 min-w-0 flex flex-col text-cream/85">
        <header className="md:hidden flex items-center justify-between px-5 py-4 border-b border-white/10" style={{ background: "#140917" }}>
          <Link to="/" className="flex items-center gap-2 font-hero text-xl text-cream" data-testid="admin-mobile-logo">
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt={branding.brand_name || "rieave"}
                className="h-7 w-auto object-contain"
                style={{ filter: "invert(1) brightness(1.1)" }}
              />
            ) : (
              <span>{branding.brand_name || "rieave"}</span>
            )}
            <span className="font-label text-[10px] uppercase tracking-[0.22em] text-cream/70">admin</span>
          </Link>
          <button onClick={() => { adminLogout(); navigate("/admin/login"); }} className="text-cream/80 hover:text-cream">
            <LogOut size={16} />
          </button>
        </header>
        <div className="flex-1 p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
